import { Button, Col, Row, Spin } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { RootState } from '../../redux'
import { currentUserLoadContractShareLink, currentUserLoadDetail, ICurrentUserLoadDetailPayload } from '../../redux/currentUser/currentUserActions'
import { IUser } from '../../types/interfaces'
import { getContractToSign } from '../../utils/helpers'
import WithdrawalsDashboardButton from './dashboard/WithdrawalsDashboardButton'
import WithdrawalsDashboardHistory from './dashboard/WithdrawalsDashboardHistory'
import WithdrawalsDashboardOverview from './dashboard/WithdrawalsDashboardOverview'

type IWithdrawalsDashboard = {}

const WithdrawalsDashboard: FC<IWithdrawalsDashboard> = () => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const isMobile = useMediaQuery({ maxWidth: 991 })
	const isDesktop = useMediaQuery({ minWidth: 992 })
	const [contractShareLink, setContractShareLink] = useState<string | null>(null)

	const user: IUser | undefined = useSelector((state: RootState) => state.userAuth.user)
	const currentUser: ICurrentUserLoadDetailPayload | undefined = useSelector((state: RootState) => state.currentUser.detail)

	useEffect(() => {
		document.title = `${t('loc:employee|Withdrawals')} - ${t('loc:general|WageNow')}`
	})

	useEffect(() => {
		if (!user) return
		dispatch(currentUserLoadDetail(user.id))
	}, [dispatch, user?.id])

	// Check if we need to fetch contract share link
	useEffect(() => {
		if (!currentUser.record?.contracts) return

		const contractToSign = getContractToSign(currentUser.record.contracts)

		if (contractToSign) {
			dispatch(
				currentUserLoadContractShareLink(
					contractToSign.employeeContractId,
					(contract) => setContractShareLink(contract.shareLink),
					() => {
						setContractShareLink(null)
					}
				)
			)
		} else {
			setContractShareLink(null)
		}
	}, [currentUser.record?.contracts, dispatch])

	if (!currentUser.record) return null

	const shouldShowContractBanner = contractShareLink && isMobile

	return currentUser.isLoading ? (
		<div className={'h-80 flex justify-center items-center'}>
			<Spin size={'large'} />
		</div>
	) : (
		<Row justify={'center'}>
			<Col xs={{ span: 24 }} xxl={{ span: 20 }}>
				<Row gutter={{ lg: 32, xxl: 64 }}>
					<Col xs={{ span: 24 }} lg={{ span: 12 }} className={'mb-6 lg:mb-0'}>
						<div className={'lg:bg-white lg:p-10 lg:rounded-md'}>
							{shouldShowContractBanner && (
								<div className='flex flex-col items-center bg-red-light py-8 px-6 xxl:px-16 rounded-md shadow-md mb-6'>
									<div className='text-22 font-700 text-center mb-3'>{t('loc:employee|Finish signing the contract to make your funds available.')}</div>
									<div className='text-base text-center mb-4'>
										{t("loc:employee|WageNow Benefit is free for employees. You don't pay for the service or withdrawals.")}
									</div>
									<Button
										type={'default'}
										shape={'round'}
										block={true}
										className={'shadow-md text-20 font-700 text-white bg-neutral-800'}
										href={contractShareLink}
										target='_blank'
									>
										{t('loc:employee|Go to contracts')}
									</Button>
								</div>
							)}
							<WithdrawalsDashboardOverview />
							{isMobile && <WithdrawalsDashboardButton />}
							<WithdrawalsDashboardHistory />
						</div>
					</Col>
					{isDesktop && (
						<Col xs={{ span: 24 }} lg={{ span: 12 }}>
							<WithdrawalsDashboardButton contractShareLink={contractShareLink} />
						</Col>
					)}
				</Row>
			</Col>
		</Row>
	)
}

export default WithdrawalsDashboard
